import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Layout } from '../components/layout';
import { ProductCard } from '../components/ProductCard';
import { Button, EmptyState, Loading, Modal } from '../components/ui';
import { api } from '../services/api';
import { useUserStore } from '../store';
import { Product, FilterState, Platform, Language, ProductCategory, Region } from '../types';
import { splitByPlatform, languageKind } from '../utils/helpers';

const PAGE_SIZE = 100;

const CATEGORIES: { value: ProductCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'game', label: 'Игры' },
  { value: 'subscription', label: 'Подписки' },
  { value: 'donat', label: 'Дополнения' },
];

const defaultFilters: FilterState = {
  platforms: [],
  priceRange: [0, 22400],
  languages: [],
  categories: [],
  regions: [],
};

// ── Persisted state per route ──────────────────────────────────────
interface CatalogPersisted {
  searchQuery?: string;
  activeCategory?: ProductCategory | 'all';
  filters?: FilterState;
  loaded?: number;
}

function catalogStateKey(collectionId?: string): string {
  return `ps_store_catalog_state:${collectionId || 'all'}`;
}

function loadCatalogState(collectionId?: string): CatalogPersisted {
  try {
    const raw = sessionStorage.getItem(catalogStateKey(collectionId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCatalogState(collectionId: string | undefined, state: CatalogPersisted) {
  try {
    sessionStorage.setItem(catalogStateKey(collectionId), JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

// In-memory cache of fetched products per collectionId — позволяет на back
// показать те же товары мгновенно, без скелетона и мелькания.
interface CatalogCacheEntry {
  products: Product[];
  total: number;
  title?: string;
}
const catalogCache = new Map<string, CatalogCacheEntry>();

export const CatalogPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { collectionId } = useParams<{ collectionId?: string }>();
  const userRegion = useUserStore((s) => s.lastSelectedRegion);

  // Восстановим persisted state из sessionStorage (если был)
  const persisted = useRef<CatalogPersisted>(loadCatalogState(collectionId)).current;

  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    persisted.searchQuery ?? searchParams.get('search') ?? '',
  );
  const [activeCategory, setActiveCategory] = useState<ProductCategory | 'all'>(
    persisted.activeCategory ?? 'all',
  );
  const [collectionTitle, setCollectionTitle] = useState<string | null>(null);

  const categoryRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useState<FilterState>(persisted.filters ?? defaultFilters);
  const [draftFilters, setDraftFilters] = useState<FilterState>(persisted.filters ?? defaultFilters);

  // Persist state on any change (filters/search/category + loaded products count)
  useEffect(() => {
    saveCatalogState(collectionId, {
      searchQuery,
      activeCategory,
      filters,
      loaded: products.length,
    });
  }, [collectionId, searchQuery, activeCategory, filters, products.length]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = collectionId ? `coll:${collectionId}` : `all:${activeCategory}`;

    // 1. Если есть свежий in-memory кеш — показываем мгновенно, без скелетона
    const cached = catalogCache.get(cacheKey);
    if (cached && cached.products.length > 0) {
      setProducts(cached.products);
      setTotalProducts(cached.total);
      if (cached.title) setCollectionTitle(cached.title);
      setIsLoadingProducts(false);
      return; // не делаем повторный fetch — данные актуальны (60-сек Redis TTL на бэке)
    }

    const loadProducts = async () => {
      setIsLoadingProducts(true);
      try {
        // Сколько товаров было загружено до ухода — догружаем до этого значения сразу
        const wantLoaded = Math.max(persisted.loaded ?? PAGE_SIZE, PAGE_SIZE);

        if (collectionId) {
          const first = await api.getCollection(collectionId, { limit: PAGE_SIZE, offset: 0 });
          if (cancelled) return;
          if (!first) {
            setProducts([]);
            setTotalProducts(0);
            return;
          }
          setCollectionTitle(first.title);
          setTotalProducts(first.total);
          let merged: Product[] = (first.products || []).filter((p: Product) => p.minPrice > 0);

          if (wantLoaded > first.products.length && first.total > first.products.length) {
            const target = Math.min(wantLoaded, first.total);
            const offsets: number[] = [];
            for (let o = first.products.length; o < target; o += PAGE_SIZE) offsets.push(o);
            const pages = await Promise.all(
              offsets.map((o) => api.getCollection(collectionId, { limit: PAGE_SIZE, offset: o })),
            );
            if (cancelled) return;
            for (const pg of pages) {
              if (!pg) continue;
              const mapped = (pg.products || []).filter((p: Product) => p.minPrice > 0);
              merged = merged.concat(mapped);
            }
          }

          setProducts(merged);
          // сохраняем в кеш для мгновенного back
          catalogCache.set(cacheKey, { products: merged, total: first.total, title: first.title });
        } else {
          const category = activeCategory !== 'all' ? activeCategory : undefined;
          const first = await api.getProducts({ category, limit: PAGE_SIZE, offset: 0 });
          if (cancelled) return;
          setTotalProducts(first.total);
          let merged: Product[] = first.products;

          if (wantLoaded > first.products.length && first.total > first.products.length) {
            const target = Math.min(wantLoaded, first.total);
            const offsets: number[] = [];
            for (let o = first.products.length; o < target; o += PAGE_SIZE) offsets.push(o);
            const pages = await Promise.all(
              offsets.map((o) => api.getProducts({ category, limit: PAGE_SIZE, offset: o })),
            );
            if (cancelled) return;
            for (const pg of pages) merged = merged.concat(pg.products);
          }

          setProducts(merged);
          catalogCache.set(cacheKey, { products: merged, total: first.total });
        }
      } finally {
        if (!cancelled) setIsLoadingProducts(false);
      }
    };
    loadProducts();
    return () => {
      cancelled = true;
    };
    // persisted.loaded читаем 1 раз на mount, не делаем зависимостью
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, collectionId]);

  const loadingMoreRef = useRef(false);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    const cacheKey = collectionId ? `coll:${collectionId}` : `all:${activeCategory}`;
    try {
      if (collectionId) {
        const col = await api.getCollection(collectionId, { limit: PAGE_SIZE, offset: products.length });
        if (col && col.products.length > 0) {
          const colProducts = col.products.filter((p: Product) => p.minPrice > 0);
          setProducts(prev => {
            const next = [...prev, ...colProducts];
            catalogCache.set(cacheKey, { products: next, total: col.total, title: col.title });
            return next;
          });
          setTotalProducts(col.total);
        }
      } else {
        const category = activeCategory !== 'all' ? activeCategory : undefined;
        const res = await api.getProducts({ category, limit: PAGE_SIZE, offset: products.length });
        if (res.products.length > 0) {
          setProducts(prev => {
            const next = [...prev, ...res.products];
            catalogCache.set(cacheKey, { products: next, total: res.total });
            return next;
          });
        }
        setTotalProducts(res.total);
      }
    } finally {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [activeCategory, products.length, collectionId]);

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loadingMoreRef.current) loadMore();
    }, { rootMargin: '400px' });
    observerRef.current.observe(node);
  }, [loadMore]);

  // Server-side search
  const [serverResults, setServerResults] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setServerResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await api.searchProducts(searchQuery.trim(), undefined, userRegion || undefined);
        setServerResults(results);
      } catch {
        setServerResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery, userRegion]);

  useEffect(() => {
    applyFilters();
  }, [products, filters, searchQuery, activeCategory, serverResults]);

  const applyFilters = () => {
    let allProducts = [...products];
    if (searchQuery.trim() && serverResults.length > 0) {
      const ids = new Set(allProducts.map(p => p.id));
      for (const p of serverResults) {
        if (!ids.has(p.id)) allProducts.push(p);
      }
    }

    let filtered = allProducts;

    if (searchQuery.trim()) {
      const normalize = (s: string) =>
        s.replace(/[®™©]+/g, '').replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
      const queryWords = normalize(searchQuery).split(' ').filter(w => w.length >= 1);
      if (queryWords.length > 0) {
        filtered = filtered.filter((p) => {
          const text = normalize(p.name) + ' ' + normalize(p.subtitle || '');
          return queryWords.every(w => text.includes(w));
        });
      }
    }

    if (activeCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === activeCategory);
    }

    if (filters.platforms.length > 0) {
      filtered = filtered.filter((p) =>
        p.variants.some(
          (v) => v.platform && filters.platforms.includes(v.platform)
        )
      );
    }

    const [minPrice, maxPrice] = filters.priceRange;
    filtered = filtered.filter(
      (p) => p.minPrice >= minPrice && p.minPrice <= maxPrice
    );

    if (filters.languages.length > 0) {
      filtered = filtered.filter((p) =>
        p.variants.some(
          (v) => { const k = languageKind(v.language); return !!k && filters.languages.includes(k); }
        )
      );
    }

    if (filters.categories.length > 0) {
      filtered = filtered.filter((p) =>
        filters.categories.includes(p.category)
      );
    }

    if (filters.regions.length > 0) {
      filtered = filtered.filter((p) =>
        p.variants.some((v) => filters.regions.includes(v.region))
      );
    }

    setFilteredProducts(filtered);
  };

  const togglePlatform = (platform: Platform) => {
    setDraftFilters((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  const toggleLanguage = (language: Language) => {
    setDraftFilters((prev) => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter((l) => l !== language)
        : [...prev.languages, language],
    }));
  };

  const toggleRegion = (region: Region) => {
    setDraftFilters((prev) => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter((r) => r !== region)
        : [...prev.regions, region],
    }));
  };

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters });
    setShowFilters(false);
  };

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters });
    setShowFilters(true);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setDraftFilters(defaultFilters);
    setSearchQuery('');
    setActiveCategory('all');
  };

  const handleResetInModal = () => {
    setDraftFilters(defaultFilters);
  };

  const activeFiltersCount =
    filters.platforms.length +
    filters.languages.length +
    filters.regions.length +
    (filters.priceRange[0] !== 0 || filters.priceRange[1] !== 22400 ? 1 : 0);

  const activeCategoryLabel = CATEGORIES.find(c => c.value === activeCategory)?.label || 'Все';

  const handleSelectCategory = (value: ProductCategory | 'all') => {
    setActiveCategory(value);
    setShowCategoryDropdown(false);
  };

  return (
    <Layout>
      <div className="px-4 pt-4 pb-6">
        {/* Search + Filters row */}
        <div className="flex items-center gap-2 mb-3">
          {/* Search */}
          <div className="flex-1 bg-background-secondary rounded-[14px] px-3.5 py-2.5 flex items-center min-w-0">
            <svg
              className="w-[16px] h-[16px] text-text-tertiary mr-2.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Поиск"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 outline-none text-text-primary text-sm bg-transparent placeholder:text-text-tertiary min-w-0"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="ml-1.5 p-0.5 text-text-tertiary hover:text-text-secondary transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Category dropdown */}
          <div className="relative flex-shrink-0" ref={categoryRef}>
            <button
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-[14px] text-sm font-medium transition-all whitespace-nowrap ${
                activeCategory !== 'all'
                  ? 'bg-primary text-white'
                  : 'bg-background-secondary text-text-secondary'
              }`}
            >
              {activeCategoryLabel}
              <svg
                className={`w-3.5 h-3.5 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Category dropdown menu */}
            {showCategoryDropdown && (
              <div className="absolute top-full right-0 mt-1.5 bg-white rounded-[14px] border border-gray-200/80 z-30 min-w-[160px] animate-fade-in overflow-hidden">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => handleSelectCategory(cat.value)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      activeCategory === cat.value
                        ? 'text-primary font-semibold bg-primary-50'
                        : 'text-text-primary hover:bg-background-secondary'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filter button */}
          <div className="relative flex-shrink-0" ref={filterRef}>
            <button
              onClick={handleOpenFilters}
              className={`flex items-center gap-1.5 px-3 py-2.5 rounded-[14px] text-sm font-medium transition-all ${
                activeFiltersCount > 0
                  ? 'bg-primary-50 text-primary'
                  : 'bg-background-secondary text-text-secondary'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              {activeFiltersCount > 0 && (
                <span className="bg-primary text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Collection title or results count */}
        {collectionTitle && (
          <p className="text-sm font-semibold text-text-primary mb-1">{collectionTitle}</p>
        )}
        {totalProducts > 0 && (
          <p className="text-xs text-text-tertiary mb-3">
            Найдено: {totalProducts}
          </p>
        )}

        {/* Products Grid */}
        {isLoadingProducts ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square rounded-[14px] bg-gray-200 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-1.5" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 && isSearching ? (
          <div className="flex justify-center py-12">
            <Loading />
          </div>
        ) : filteredProducts.length === 0 ? (
          <EmptyState
            title="Ничего не найдено"
            description="Попробуйте изменить параметры поиска или фильтры"
            action={{
              label: 'Сбросить фильтры',
              onClick: resetFilters,
            }}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {/* В поиске версии «для PS4» и «для PS4/PS5» одной игры — отдельными плитками */}
              {(searchQuery.trim()
                ? filteredProducts.flatMap((p) => splitByPlatform(p, userRegion))
                : filteredProducts.map((p) => ({ product: p, variant: undefined }))
              ).map(({ product, variant }) => (
                <ProductCard key={variant ? `${product.id}:${variant.id}` : product.id} product={product} variant={variant} orientation="vertical" />
              ))}
            </div>
            {products.length < totalProducts && (
              <div ref={sentinelRef} className="flex justify-center py-6">
                <svg className="animate-spin" width={28} height={28} viewBox="0 0 50 50" fill="none">
                  <circle cx="25" cy="25" r="20" stroke="#E5E7EB" strokeWidth="4" />
                  <path d="M25 5 A20 20 0 0 1 45 25" stroke="#4361EE" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </div>
            )}
          </>
        )}
      </div>

      {/* Filters Modal */}
      <Modal
        isOpen={showFilters}
        onClose={() => { setDraftFilters({ ...filters }); setShowFilters(false); }}
        title="Фильтры"
        dismissable={false}
        footer={
          <div className="space-y-1.5">
            <Button variant="primary" size="sm" fullWidth onClick={handleApplyFilters}>Применить</Button>
            <Button variant="secondary" size="sm" fullWidth onClick={handleResetInModal}>Очистить все фильтры</Button>
          </div>
        }
      >
        <div className="space-y-5">
          {/* Platform */}
          <div>
            <h3 className="text-sm font-semibold text-text-primary mb-2.5">Платформа</h3>
            <div className="flex flex-wrap gap-2">
              {(['PS4', 'PS5'] as Platform[]).map((platform) => (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border-[1.5px] transition-all text-sm font-medium ${
                    draftFilters.platforms.includes(platform)
                      ? 'border-primary bg-primary-50 text-primary'
                      : 'border-gray-200 text-text-secondary'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 ${
                    draftFilters.platforms.includes(platform)
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                  }`}>
                    {draftFilters.platforms.includes(platform) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-text-primary">Цена</h3>
              <span className="text-xs text-text-secondary">
                {draftFilters.priceRange[0]} ₽ – {draftFilters.priceRange[1]} ₽
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="22400"
              step="100"
              value={draftFilters.priceRange[1]}
              onChange={(e) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  priceRange: [0, parseInt(e.target.value)],
                }))
              }
              className="w-full accent-primary"
            />
          </div>

          {/* Language */}
          <div>
            <h3 className="text-sm font-semibold text-text-primary mb-2.5">Язык</h3>
            <div className="flex flex-col gap-2">
              {[
                { value: 'ru_subtitles' as Language, label: 'Русские субтитры' },
                { value: 'ru_full' as Language, label: 'Полностью на русском' },
              ].map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => toggleLanguage(lang.value)}
                  className={`inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xl border-[1.5px] transition-all text-sm font-medium text-left ${
                    draftFilters.languages.includes(lang.value)
                      ? 'border-primary bg-primary-50 text-primary'
                      : 'border-gray-200 text-text-secondary'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 ${
                    draftFilters.languages.includes(lang.value)
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                  }`}>
                    {draftFilters.languages.includes(lang.value) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Region */}
          <div>
            <h3 className="text-sm font-semibold text-text-primary mb-2.5">Регион</h3>
            <div className="flex flex-wrap gap-2">
              {(['TR', 'IN', 'UA'] as Region[]).map((region) => (
                <button
                  key={region}
                  onClick={() => toggleRegion(region)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border-[1.5px] transition-all text-sm font-medium ${
                    draftFilters.regions.includes(region)
                      ? 'border-primary bg-primary-50 text-primary'
                      : 'border-gray-200 text-text-secondary'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 ${
                    draftFilters.regions.includes(region)
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                  }`}>
                    {draftFilters.regions.includes(region) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {region}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};
